import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ============================================================================
// useBoqGridLayout — shared AG Grid layout behaviour for the QS workspace
// ============================================================================
// Gives every QS grid the same Team-Overview-style controls:
//   • per-column collapse to a sliver (clamped via width/min/maxWidth so the
//     columns to the right slide left) and freeze (pin left), driven from the
//     custom header (BoqGridHeader) through grid context;
//   • a remembered layout (column widths, order, collapsed + frozen columns)
//     persisted to localStorage so the user's preferred view survives reloads;
//   • a single, left-pinned selection checkbox via AG Grid v33's selection API
//     (use the exported QS_ROW_SELECTION / QS_SELECTION_COL_DEF).
//
// Usage:
//   const grid = useBoqGridLayout({ storageKey, defaultWidths: COL_W,
//                                   toggleableColIds: [...], staticColIds: [...] });
//   <AgGridReact ref={grid.gridRef} context={grid.gridContext}
//      columnDefs={grid.decorateColumns(baseDefs)}
//      onColumnResized={grid.onColumnResized} onColumnMoved={grid.onColumnMoved}
//      onGridReady={(p) => { grid.applySavedOrder(p.api); /* … */ }}
//      rowSelection={QS_ROW_SELECTION} selectionColumnDef={QS_SELECTION_COL_DEF} />
// ============================================================================

export const COLLAPSED_W = 32;   // sliver width — just enough for the expand chevron

// Single-checkbox selection (AG Grid v33). Pass these straight to the grid so
// the checkbox lives in one dedicated left column, not on every leading column.
export const QS_ROW_SELECTION = { mode: 'multiRow', checkboxes: true, headerCheckbox: true, enableClickSelection: false };
export const QS_SELECTION_COL_DEF = { pinned: 'left', width: 44, maxWidth: 44, resizable: false, suppressMovable: true, lockPosition: true };

export function useBoqGridLayout({ storageKey, defaultWidths = {}, toggleableColIds = [], staticColIds = [], gridRef: externalRef, orderVersion = 1 } = {}) {
  // Let callers pass their own ref (so it stays a stable useRef for them);
  // otherwise own one here.
  const internalRef = useRef(null);
  const gridRef = externalRef || internalRef;

  const staticSet = useMemo(
    () => (staticColIds instanceof Set ? staticColIds : new Set(staticColIds)),
    [staticColIds]
  );
  const toggleable = useMemo(() => new Set(toggleableColIds), [toggleableColIds]);

  const saved = useRef(((() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  })())).current;

  const [collapsedColIds, setCollapsedColIds] = useState(() => new Set(saved.collapsed || []));
  const [lockedColIds, setLockedColIds] = useState(() => new Set(saved.locked || []));
  const [colWidths, setColWidths] = useState(() => ({ ...defaultWidths, ...(saved.widths || {}) }));
  // A page bumps orderVersion when it changes its default column order; a
  // remembered order from before then is dropped (widths and the rest stay),
  // or it would keep hiding the new default.
  const [colOrder, setColOrder] = useState(() => (
    Array.isArray(saved.order) && (saved.orderVersion || 1) === orderVersion ? saved.order : null));

  // Persist the layout whenever any part of it changes.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        collapsed: [...collapsedColIds], locked: [...lockedColIds], widths: colWidths, order: colOrder, orderVersion,
      }));
    } catch { /* storage full / unavailable — layout just won't persist */ }
  }, [storageKey, collapsedColIds, lockedColIds, colWidths, colOrder, orderVersion]);

  const toggleCollapse = useCallback((colId) => {
    setCollapsedColIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId); else next.add(colId);
      return next;
    });
  }, []);

  const toggleLock = useCallback((colId) => {
    setLockedColIds((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId); else next.add(colId);
      return next;
    });
  }, []);

  const resetLayout = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setCollapsedColIds(new Set());
    setLockedColIds(new Set());
    setColWidths({ ...defaultWidths });
    setColOrder(null);
  }, [storageKey, defaultWidths]);

  const gridContext = useMemo(() => ({
    collapsedColIds, lockedColIds, staticColIds: staticSet, toggleCollapse, toggleLock,
  }), [collapsedColIds, lockedColIds, staticSet, toggleCollapse, toggleLock]);

  // Apply remembered widths to every column, clamp collapsed columns to a
  // sliver, pin frozen ones left, and honour the remembered column order.
  const decorateColumns = useCallback((base) => {
    const defs = base.map((def) => {
      const id = def.colId || def.field;
      const savedW = colWidths[id];
      if (!toggleable.has(id)) {
        return savedW ? { ...def, width: savedW } : def;
      }
      const isC = collapsedColIds.has(id);
      const isL = lockedColIds.has(id);
      return {
        ...def,
        width: isC ? COLLAPSED_W : (savedW || def.width),
        minWidth: isC ? COLLAPSED_W : (def.minWidth ?? 44),
        maxWidth: isC ? COLLAPSED_W : def.maxWidth,
        resizable: !isC,
        suppressSizeToFit: isC,
        pinned: isL ? 'left' : (def.pinned ?? null),
        headerClass: isC ? 'qs-col-collapsed' : def.headerClass,
        cellClass: isC ? 'qs-cell-collapsed' : def.cellClass,
      };
    });
    if (colOrder && colOrder.length) {
      const rank = (id) => { const i = colOrder.indexOf(id); return i === -1 ? 999 : i; };
      defs.sort((a, b) => rank(a.colId || a.field) - rank(b.colId || b.field));
    }
    return defs;
  }, [toggleable, colWidths, collapsedColIds, lockedColIds, colOrder]);

  // Re-apply the saved order once the grid is ready (the columnDefs sort above
  // covers rebuilds; this catches the very first paint and pinned columns).
  const applySavedOrder = useCallback((api) => {
    if (colOrder && colOrder.length) {
      api.applyColumnState({ state: colOrder.map((colId) => ({ colId })), applyOrder: true });
    }
  // colOrder is intentionally read once on (re)mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember a manual resize (but never the programmatic collapse to a sliver).
  const onColumnResized = useCallback((e) => {
    if (!e.finished || e.source !== 'uiColumnResized') return;
    const updates = {};
    (e.columns || []).forEach((c) => {
      const id = c.getColId();
      if (!collapsedColIds.has(id)) updates[id] = c.getActualWidth();
    });
    if (Object.keys(updates).length) setColWidths((w) => ({ ...w, ...updates }));
  }, [collapsedColIds]);

  // Remember a manual column reorder.
  const onColumnMoved = useCallback((e) => {
    if (!e.finished) return;
    const api = gridRef.current?.api;
    if (api) setColOrder(api.getColumnState().map((s) => s.colId));
  }, [gridRef]);

  return {
    gridRef, gridContext, decorateColumns, applySavedOrder,
    onColumnResized, onColumnMoved, resetLayout,
    collapsedColIds, lockedColIds, colWidths, colOrder,
  };
}

export default useBoqGridLayout;
